import { ExternalLinkIcon } from "lucide-react";
import { useCallback } from "react";

import { useI18n, type MessageKey } from "~/i18n";
import { readLocalApi } from "../../localApi";
import { Button } from "../ui/button";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";
import { searchableSetting, type SettingsSearchItemId } from "./settingsSearch";
import {
  mailtoHref,
  MORROW_CONTACT,
  MORROW_LEGAL_URLS,
  MORROW_SUPPORT_URLS,
} from "./morrowSupportLegal";

function openExternalUrl(url: string) {
  void readLocalApi()?.shell.openExternal(url);
}

function ExternalSettingsRow({
  id,
  description,
  url,
  controlLabel,
}: {
  readonly id: SettingsSearchItemId;
  readonly description: string;
  readonly url: string;
  readonly controlLabel: string;
}) {
  const { t } = useI18n();
  const setting = searchableSetting(id, t);
  const handleOpen = useCallback(() => {
    openExternalUrl(url);
  }, [url]);

  return (
    <SettingsRow
      {...setting}
      description={description}
      control={
        <Button
          size="xs"
          variant="outline"
          aria-label={`${controlLabel}: ${setting.title}`}
          onClick={handleOpen}
        >
          {controlLabel}
          <ExternalLinkIcon />
        </Button>
      }
    />
  );
}

export function SupportSettingsPanel() {
  const { t } = useI18n();
  const emailLabel = t("settings.support.email");
  const openLabel = t("settings.support.open");

  return (
    <SettingsPageContainer>
      <SettingsSection id="support-help" title={t("settings.support.getHelp")}>
        <ExternalSettingsRow
          id="help-center"
          description={t("settings.support.helpCenter.description")}
          url={MORROW_SUPPORT_URLS.helpCenter}
          controlLabel={openLabel}
        />
        <ExternalSettingsRow
          id="submit-request"
          description={t("settings.support.submitRequest.description")}
          url={MORROW_SUPPORT_URLS.submitRequest}
          controlLabel={openLabel}
        />
        <ExternalSettingsRow
          id="system-status"
          description={t("settings.support.systemStatus.description")}
          url={MORROW_SUPPORT_URLS.systemStatus}
          controlLabel={openLabel}
        />
      </SettingsSection>

      <SettingsSection id="support-topics" title={t("settings.support.topics")}>
        <ExternalSettingsRow
          id="support-setup"
          description={t("settings.support.topic.setup.description")}
          url={MORROW_SUPPORT_URLS.setup}
          controlLabel={openLabel}
        />
        <ExternalSettingsRow
          id="support-integrations"
          description={t("settings.support.topic.integrations.description")}
          url={MORROW_SUPPORT_URLS.integrations}
          controlLabel={openLabel}
        />
        <ExternalSettingsRow
          id="support-memory"
          description={t("settings.support.topic.memory.description")}
          url={MORROW_SUPPORT_URLS.companyMemory}
          controlLabel={openLabel}
        />
        <ExternalSettingsRow
          id="support-security"
          description={t("settings.support.topic.security.description")}
          url={MORROW_SUPPORT_URLS.securityPrivacy}
          controlLabel={openLabel}
        />
        <ExternalSettingsRow
          id="support-billing"
          description={t("settings.support.topic.billing.description")}
          url={MORROW_SUPPORT_URLS.billing}
          controlLabel={openLabel}
        />
        <ExternalSettingsRow
          id="support-faq"
          description={t("settings.support.topic.faq.description")}
          url={MORROW_SUPPORT_URLS.faq}
          controlLabel={openLabel}
        />
      </SettingsSection>

      <SettingsSection id="support-contact" title={t("settings.support.contact")}>
        <ExternalSettingsRow
          id="support-email"
          description={MORROW_CONTACT.supportEmail}
          url={mailtoHref(MORROW_CONTACT.supportEmail)}
          controlLabel={emailLabel}
        />
        <ExternalSettingsRow
          id="support-security-email"
          description={MORROW_CONTACT.securityEmail}
          url={mailtoHref(MORROW_CONTACT.securityEmail)}
          controlLabel={emailLabel}
        />
        <ExternalSettingsRow
          id="support-legal-email"
          description={MORROW_CONTACT.legalEmail}
          url={mailtoHref(MORROW_CONTACT.legalEmail)}
          controlLabel={emailLabel}
        />
        <ExternalSettingsRow
          id="support-phone"
          description={MORROW_CONTACT.phoneDisplay}
          url={MORROW_CONTACT.phoneTel}
          controlLabel={t("settings.support.call")}
        />
        <ExternalSettingsRow
          id="support-book-call"
          description={t("settings.support.bookCall.description")}
          url={MORROW_SUPPORT_URLS.bookCall}
          controlLabel={openLabel}
        />
      </SettingsSection>

      <SettingsSection id="legal-documents" title={t("settings.legal.documents")}>
        {LEGAL_DOCUMENTS.map((document) => (
          <ExternalSettingsRow
            key={document.id}
            id={document.id}
            description={t(document.descriptionKey)}
            url={document.url}
            controlLabel={openLabel}
          />
        ))}
      </SettingsSection>

      <SettingsSection id="legal-company" title={t("settings.legal.company")}>
        <SettingsRow
          {...searchableSetting("legal-company", t)}
          description={t("settings.legal.company.description")}
          control={
            <Button
              size="xs"
              variant="outline"
              aria-label={`${openLabel}: ${t("settings.search.legalCompany")}`}
              onClick={() => openExternalUrl(MORROW_LEGAL_URLS.company)}
            >
              {openLabel}
              <ExternalLinkIcon />
            </Button>
          }
        />
      </SettingsSection>
    </SettingsPageContainer>
  );
}

const LEGAL_DOCUMENTS: ReadonlyArray<{
  readonly id: SettingsSearchItemId;
  readonly descriptionKey: MessageKey;
  readonly url: string;
}> = [
  {
    id: "privacy-policy",
    descriptionKey: "settings.legal.privacy.description",
    url: MORROW_LEGAL_URLS.privacy,
  },
  {
    id: "terms-of-service",
    descriptionKey: "settings.legal.terms.description",
    url: MORROW_LEGAL_URLS.terms,
  },
  {
    id: "dpa",
    descriptionKey: "settings.legal.dpa.description",
    url: MORROW_LEGAL_URLS.dpa,
  },
  {
    id: "cookie-policy",
    descriptionKey: "settings.legal.cookies.description",
    url: MORROW_LEGAL_URLS.cookies,
  },
  {
    id: "trust-center",
    descriptionKey: "settings.legal.trust.description",
    url: MORROW_LEGAL_URLS.trust,
  },
];
